// src/theme/plugins/caro-editorial-theme.js
//
// Caro Editorial — MUI mapping of the shared tokens (spec 20260930-caro-editorial-theme, T029,
// plan §7.2). The tokens themselves live in @caro/shared/theme (CARO_EDITORIAL_TOKENS); this file
// only decides how MUI shows them.
//
// Deliberate differences from the owner's source file (plan §4):
// - Component overrides are STYLE CALLBACKS on `theme.palette`, not light-mode literals, so light
//   and dark share one code path (IR-2) and are deep-merged into the base overrides (IR-1).
// - The Paper radius sits on the `rounded` slot, not on `root` (IR-3): AppBar, menus and the
//   sidebar stay square-edged.
// - NO `MuiDrawer` override (IR-4, LD-7): only the navigation sidebar is aubergine, via
//   `theme.caro.sidebar` in Layout.js. Detail panels, filters and bottom sheets stay paper.
// - Filled success buttons sit on `success.dark` (LD-14; white on success.main is 3.56:1).
// - `MuiChip` renders the status pairs (T067, below): every chip colour, filled or outlined.
import { CARO_EDITORIAL_TOKENS, CHIP_COLOR_TO_STATUS } from '@caro/shared/theme';

const T = CARO_EDITORIAL_TOKENS;

/** Build the Editorial plugin for one mode. `baseFontSize` comes from the textSize setting. */
export function buildEditorialTheme(mode = 'light') {
  const tok = mode === 'dark' ? T.dark : T.light;
  const { typography: ty, radius, customShadows, gradients } = T;

  const heading = {
    fontFamily: ty.fontFamilyDisplay,
    fontWeight: ty.headingWeight,
    lineHeight: ty.headingLineHeight,
    letterSpacing: ty.headingLetterSpacing,
  };

  return {
    // create-theme.js must not paint its neutral dark greys over this palette (F-3).
    ownsDarkPalette: true,
    palette: {
      primary: tok.primary,
      secondary: tok.secondary,
      success: tok.success,
      info: tok.info,
      warning: tok.warning,
      error: tok.error,
      grey: tok.grey,
      background: tok.background,
      text: tok.text,
      divider: tok.divider,
      dividerSoft: tok.dividerSoft,
      action: tok.action,
    },
    typography: {
      fontFamily: ty.fontFamily,
      fontFamilyDisplay: ty.fontFamilyDisplay,
      fontFamilyMono: ty.fontFamilyMono,
      h1: heading,
      h2: heading,
      h3: heading,
      h4: heading,
      h5: heading,
      h6: heading,
      overline: { ...ty.overline },
      button: { fontWeight: 800 },
    },
    // LD-5: under Editorial the fonts are fixed; the user's typography choice is ignored (and kept).
    locksTypography: true,
    shape: { borderRadius: radius.md, radius },
    customShadows,
    gradients,
    status: tok.status,
    sidebar: { ...tok.sidebar, solid: true },
    radius,
    // Replacements for hard-coded shell literals (phase 4). Code reads
    // `theme.caro.legacy.X ?? <old literal>`: plum/gold/presets have no entry and keep the literal,
    // Editorial maps it onto its tokens.
    legacy: {
      presence: { online: tok.success.main, offline: tok.grey[600] },
      settingsPanelBg: tok.background.subtle,
      settingsRowHoverBg: tok.action.hover,
      settingsEmailActiveBg: tok.status.available.bg,
      settingsPushActiveBg: tok.status.brand.bg,
      pageHeaderTitle: { fontWeight: ty.headingWeight, lineHeight: ty.headingLineHeight },
      // pages/Health.js (T054): the standalone status page follows the theme instead of slate.
      'health.pageBg': tok.background.default,
      'health.panelBg': tok.background.paper,
      'health.panelBgSubtle': tok.background.subtle,
      'health.panelBorder': tok.divider,
      'health.panelBorderHover': tok.primary.light,
      'health.accent': tok.primary.main,
      'health.text': tok.text.primary,
      'health.textMuted': tok.text.secondary,
      'health.textSubtle': tok.text.secondary,
      // pages/EinkDesigner (T061): editor chrome; the e-ink canvas stays black/white.
      'eink.accent': tok.primary.main,
      'eink.accentSoft': tok.action.focus,
      'eink.border': tok.divider,
    },
    // Hand-picked status colours on pages become this theme's status pairs (theme/legacy.js).
    statusChips: true,
    components: {
      MuiCssBaseline: {
        // ⚠ CssBaseline calls `styleOverrides(theme)` with the THEME itself, unlike component
        // slots, which receive `{ theme, ownerState }`. Accept both.
        styleOverrides: (arg) => {
          const theme = arg?.palette ? arg : arg?.theme;
          return {
          body: {
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
          },
          };
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: { borderRadius: radius.md },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: radius.lg,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: customShadows.card,
          }),
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            fontWeight: 800,
            textTransform: 'none',
          },
          containedPrimary: ({ theme }) => ({
            '&:hover': { backgroundColor: theme.palette.primary.dark },
          }),
          containedSuccess: ({ theme }) => ({
            backgroundColor: theme.palette.success.dark,
            '&:hover': { backgroundColor: theme.palette.success.darker },
          }),
          outlined: ({ theme }) => ({
            borderColor: theme.palette.divider,
            color: theme.palette.text.tertiary,
            '&:hover': {
              borderColor: theme.palette.primary.light,
              backgroundColor: theme.palette.action.hover,
            },
          }),
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: radius.lg },
        },
      },
      // Chips → status pairs (T067, LD-12, plan §7.12.2): every colour class, filled or outlined,
      // renders the pair CHIP_COLOR_TO_STATUS names — full text colour on a tint, never
      // transparent, icon and delete icon in the text colour. Chips without `color` are `default`
      // → neutral. This reaches all 484 <Chip color=…> call sites, dynamic ones included, without
      // touching them. Plum, gold and the presets have NO MuiChip override (T010 snapshots).
      MuiChip: {
        styleOverrides: {
          root: ({ ownerState }) => {
            const kind = CHIP_COLOR_TO_STATUS[ownerState?.color] || CHIP_COLOR_TO_STATUS.default;
            const pair = tok.status[kind];
            return {
              borderRadius: radius.pill,
              fontWeight: 700,
              backgroundColor: pair.bg,
              color: pair.fg,
              border: `1px solid ${pair.border}`,
              '& .MuiChip-icon': { color: pair.fg },
              '& .MuiChip-deleteIcon': { color: pair.fg, '&:hover': { color: pair.fg, opacity: 0.75 } },
              '&.MuiChip-clickable:hover, &.MuiChip-clickable.Mui-focusVisible': {
                backgroundColor: pair.border,
              },
            };
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: radius.md,
            backgroundColor: theme.palette.background.paper,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.light },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
          }),
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.background.neutral,
            '& .MuiTableCell-root': { color: theme.palette.text.secondary, fontWeight: 700 },
          }),
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: ({ theme }) => ({ borderBottom: `1px solid ${theme.palette.dividerSoft}` }),
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: ({ theme }) => ({
            height: 10,
            borderRadius: radius.pill,
            backgroundColor: theme.palette.dividerSoft,
          }),
          bar: { borderRadius: radius.pill },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: ({ theme }) => ({
            '&.Mui-checked + .MuiSwitch-track': { backgroundColor: theme.palette.primary.main, opacity: 1 },
          }),
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ theme }) => ({
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.inverse : T.light.text.primary,
            color: T.light.text.onInverse,
            borderRadius: 8,
          }),
        },
      },
    },
  };
}

export default buildEditorialTheme;
